'use client';

import { useCallback, useEffect, useState } from 'react';
import { usePathname } from 'next/navigation';
import nextDynamic from 'next/dynamic';
import {
  appEnv,
  isProduction,
  isStaging,
  isPredictorEnabled
} from '@/config/config';
import { getAuthStatus } from '@/utils/cookieManager';

const DynamicWhatsAppWidget = nextDynamic(
  () => import('@/components/organisms/whatsappWidget'),
  {
    ssr: false
  }
);

const DynamicSocketProvider = nextDynamic(
  () => import('@/components/NotificationSocketProvider'),
  {
    ssr: false
  }
);

const DynamicFacebookPixel = nextDynamic(
  () => import('@/components/organisms/FacebookPixelWrapper'),
  {
    ssr: false
  }
);

const DynamicChatbotGate = nextDynamic(
  () => import('@/components/organisms/chatbotGate'),
  {
    ssr: false
  }
);

const DynamicPredictorWidget = nextDynamic(
  () => import('@/components/organisms/PredictorWidget'),
  {
    ssr: false
  }
);

// BeeBot runs on development, staging and production. Production was enabled
// once chatbot-service-prod was live (2026-11-05). Every environment supplies
// what the widget needs: NEXT_PUBLIC_APP_ENV and NEXT_PUBLIC_CHATBOT_API_URL are
// both passed as build args in gcp-deploy.yml, so no deploy change is needed.
// A local run sets no APP_ENV and is not a release build, so it stays enabled.
const normalisedAppEnv = appEnv?.toLowerCase();

const isChatbotEnabled =
  normalisedAppEnv === 'development' ||
  normalisedAppEnv === 'staging' ||
  normalisedAppEnv === 'production' ||
  isStaging ||
  (!appEnv && !isProduction);

export default function ClientWidgets() {
  const [mounted, setMounted] = useState(false);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const pathname = usePathname();

  const verifyAuth = useCallback(() => {
    const authStatus = getAuthStatus();
    setIsAuthenticated(authStatus.isAuthenticated);
  }, []);

  useEffect(() => {
    setMounted(true);
    verifyAuth();

    const reverifyAuth = () => verifyAuth();
    window.addEventListener('focus', reverifyAuth);
    window.addEventListener('authLogout', reverifyAuth);
    document.addEventListener('visibilitychange', reverifyAuth);

    return () => {
      window.removeEventListener('focus', reverifyAuth);
      window.removeEventListener('authLogout', reverifyAuth);
      document.removeEventListener('visibilitychange', reverifyAuth);
    };
  }, [verifyAuth]);

  useEffect(() => {
    verifyAuth();
  }, [pathname, verifyAuth]);

  // Don't render anything on the server to avoid hydration mismatch
  if (!mounted) {
    return null;
  }

  const nonAppRoutes = [
    '/login',
    '/sign-up',
    '/signup',
    '/register',
    '/forgot-password',
    '/reset-password',
    '/otp',
    '/verification',
    '/onboarding',
    '/create-profile',
    '/auth'
  ];
  const normalisedPathname = pathname?.replace(/\/+$/, '') || '/';
  const isAuthRoute = nonAppRoutes.some(
    (route) =>
      normalisedPathname === route || normalisedPathname.startsWith(`${route}/`)
  );
  const shouldShowPredictor =
    isPredictorEnabled && isAuthenticated && !isAuthRoute;

  return (
    <>
      <DynamicWhatsAppWidget />
      <DynamicSocketProvider />
      <DynamicFacebookPixel />
      {isChatbotEnabled ? <DynamicChatbotGate /> : null}
      {shouldShowPredictor && <DynamicPredictorWidget />}
    </>
  );
}
